import { GetTimeslotsResponse } from '@src/@data/types/reservations';
import { toastOnError } from '@src/@data/utility/errors';
import { useMessages } from '@src/@data/utility/hooks/useMessages';
import { toastOnInfo } from '@src/@data/utility/toasts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { StepUrlState } from '../sharedUtils';
import { DateAndTimeRowData } from './date-and-time-form-group/DateAndTimeRow';
import { TimeslotOption } from './TimeSelector';

export const useAdditionalDateAndTimeRows = () => {
  const [rows, setRows] = useState<
    Pick<DateAndTimeRowData, 'id' | 'startTime'>[]
  >([]);

  const addRow = useCallback(() => {
    setRows((prev) => [
      ...prev,
      {
        id: prev.length + 1,
        startTime: undefined,
      },
    ]);
  }, []);

  const removeRow = useCallback((id) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  }, []);

  const updateRow = useCallback((id: number, startTime?: Date) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { id, startTime } : r)));
  }, []);

  return {
    additionalDateAndTimeRows: rows,
    addDateAndTimeRow: addRow,
    removeDateAndTimeRow: removeRow,
    updateDateAndTimeRow: updateRow,
  };
};

export const useTimeslots = ({
  isReady,
  getTimeslots,
  initialTimeslot,
  resetStartTime,
  shouldResetStartTimeOnLoading = true,
}: {
  isReady: boolean;
  getTimeslots: () => Promise<GetTimeslotsResponse | undefined>;
  initialTimeslot?: string;
  resetStartTime: () => void;
  shouldResetStartTimeOnLoading?: boolean;
}) => {
  const intl = useIntl();
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [timeslots, setTimeslots] = useState<TimeslotOption[]>();
  const shouldClearInitialTimeslot = count > 1;
  const parsedTimeslots = useMemo(() => {
    if (!initialTimeslot || shouldClearInitialTimeslot) {
      return timeslots;
    }
    const initialTimeslotDate = new Date(initialTimeslot);
    const initialTimeslotData = timeslots?.find(
      (t) => t.value === initialTimeslotDate
    );
    const initialTimeslotOption = {
      value: new Date(initialTimeslot),
      label: intl.formatTime(initialTimeslot),
      slotId: initialTimeslotData?.slotId,
      duration: initialTimeslotData?.duration,
      tickets: initialTimeslotData?.tickets,
    };
    if (timeslots?.some((t) => t.label === initialTimeslotOption.label)) {
      return timeslots;
    }
    return [initialTimeslotOption, ...(timeslots || [])].sort((t1, t2) =>
      t1.label.localeCompare(t2.label)
    );
  }, [initialTimeslot, intl, shouldClearInitialTimeslot, timeslots]);

  useEffect(() => {
    if (!isReady) {
      return;
    }
    setIsLoading(true);

    getTimeslots()
      .then((res) => {
        if (!res) {
          return;
        }
        if (!res.calendars.length) {
          setTimeslots([]);
          return;
        }
        setTimeslots(
          res.calendars
            .map((calendar) =>
              calendar.timeslots.map(
                ({ startTime, slotId, duration, tickets }) => ({
                  value: new Date(startTime),
                  label: intl.formatTime(startTime),
                  slotId,
                  duration,
                  tickets,
                })
              )
            )
            .flat()
            .sort((t1, t2) => t1.label.localeCompare(t2.label))
        );
      })
      .catch(toastOnError)
      .finally(() => {
        setCount((prev) => prev + 1);
        setIsLoading(false);
      });
  }, [getTimeslots, intl, isReady]);

  useEffect(() => {
    if (
      isLoading &&
      (initialTimeslot
        ? shouldClearInitialTimeslot
        : shouldResetStartTimeOnLoading)
    ) {
      resetStartTime();
    }
  }, [
    initialTimeslot,
    isLoading,
    resetStartTime,
    shouldClearInitialTimeslot,
    shouldResetStartTimeOnLoading,
  ]);

  return { timeslots: parsedTimeslots, areTimeslotsLoading: isLoading };
};

export const useTimeslotCheckOnCalendarInteraction = ({
  urlState,
  areTimeslotsLoading,
  timeslots,
  setStartTime,
  isReady = true,
}: {
  urlState?: StepUrlState;
  isReady?: boolean;
  areTimeslotsLoading: boolean;
  timeslots?: TimeslotOption[];
  setStartTime: (startTime: Date | undefined) => void;
}) => {
  const { messages } = useMessages();
  const intl = useIntl();
  const history = useHistory();

  useEffect(() => {
    if (!urlState || !isReady || areTimeslotsLoading || !timeslots) {
      return;
    }

    if (
      !timeslots.some(
        (t) => t.value.toString() === urlState.startTime.toString()
      )
    ) {
      setStartTime(undefined);
      toastOnInfo(
        `${messages(
          'add_reservation_timeslot_not_available'
        )} ${intl.formatTime(urlState.startTime)}`
      );
    }
    // @ts-ignore clears urlState
    history.replace();
  }, [
    areTimeslotsLoading,
    history,
    intl,
    messages,
    isReady,
    setStartTime,
    timeslots,
    urlState,
  ]);
};
